import apiClient from '../../shared/services/apiClient';
import { reportPurchase } from '../utils/adsConversion';

/**
 * Billing Service (User Version)
 *
 * Handles user billing operations including subscriptions, balance, invoices, and transactions.
 * Users can view subscription info, manage balance, and pay invoices.
 *
 * All routes use /api/user/* prefix
 */
export const billingService = {
    cancelDowngrade: async (subscriptionId) => {
        try {
            const response = await apiClient.post('/api/user/subscription-info/cancel-downgrade', { subscription_id: subscriptionId });
            return response.data;
        } catch (error) {
            return error.response?.data || { success: false, error: error.message };
        }
    },

    // ==========================================
    // Subscription Management
    // ==========================================

    /**
     * Get subscription overview
     * @param {string} productType - Product type
     * @returns {Promise} Subscription overview
     */
    getSubscriptionOverview: async (productType, resourceId) => {
        try {
            const params = new URLSearchParams({ product_type: productType });
            if (resourceId) {
                params.append('resource_id', resourceId);
            }
            const response = await apiClient.get(`/api/user/subscription-info/overview?${params.toString()}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get available upgrade plans
     * @param {string} productType - Product type
     * @param {number} resourceId - Optional resource ID (domain_id or zone_id)
     * @returns {Promise} Available upgrades
     */
    getAvailableUpgrades: async (productType, resourceId = null) => {
        try {
            let url = `/api/user/subscription-info/available-upgrades?product_type=${productType}`;
            if (resourceId) {
                url += `&resource_id=${resourceId}`;
            }
            const response = await apiClient.get(url);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // Balance Management
    // ==========================================

    /**
     * Get balance information
     * @returns {Promise} Balance data
     */
    getBalance: async () => {
        try {
            const response = await apiClient.get('/api/user/balance');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Create a balance top-up invoice. The returned invoice is then paid
     * through the normal invoice payment flow (Stripe card / saved card).
     * @param {number} amount - Top-up amount (USD)
     * @returns {Promise} { invoice_id, invoice_number, amount, currency }
     */
    createTopup: async (amount) => {
        try {
            const response = await apiClient.post('/api/user/balance/topup', { amount });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get transaction history
     * @param {Object} params - Query parameters
     * @returns {Promise} Transaction list
     */
    getTransactions: async (params) => {
        try {
            const response = await apiClient.get('/api/user/balance/transactions', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // Subscription Management (Full CRUD)
    // ==========================================

    /**
     * Get all subscriptions
     * @param {Object} params - Query parameters
     * @returns {Promise} Subscription list
     */
  getSubscriptions: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/user/subscriptions', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

    /**
     * Get single subscription details
     * @param {number} subscriptionId - Subscription ID
     * @returns {Promise} Subscription details
     */
    getSubscription: async (subscriptionId) => {
        try {
            const response = await apiClient.get('/api/user/subscriptions/get', {
                params: { id: subscriptionId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },


    // ==========================================
    // Invoice Management
    // ==========================================

    /**
     * Get invoice list
     * @param {Object} params - Query parameters
     * @returns {Promise} Invoice list
     */
    getInvoices: async (params) => {
        try {
            const response = await apiClient.get('/api/user/invoices', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get single invoice details
     * @param {number} invoiceId - Invoice ID
     * @returns {Promise} Invoice details
     */
    getInvoice: async (invoiceId) => {
        try {
            const response = await apiClient.get('/api/user/invoices/get', {
                params: { id: invoiceId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get invoice statistics
     * @returns {Promise} Invoice stats
     */
    getInvoiceStats: async () => {
        try {
            const response = await apiClient.get('/api/user/invoices/stats');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Pay invoice
     * @param {number} invoiceId - Invoice ID
     * @param {string} paymentMethod - Payment method
     * @returns {Promise} Payment result
     */
    payInvoices: async (invoiceIds) => {
        try {
            const response = await apiClient.post('/api/user/invoices/pay-batch', { ids: invoiceIds });
            // 购买转化:逐张上报已付的(金额/币种按账单)
            for (const pid of response.data?.data?.paid || []) {
                billingService.getInvoice(pid).then((r) => r?.success && reportPurchase(r.data)).catch(() => {});
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    cancelInvoice: async (invoiceId) => {
        try {
            const response = await apiClient.post('/api/user/invoices/cancel', { id: invoiceId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    payInvoice: async (invoiceId, paymentMethod) => {
        try {
            const response = await apiClient.post('/api/user/invoices/pay', {
                id: invoiceId,
                payment_method: paymentMethod
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 切换订阅自动续费开关
     * @param {number} subscriptionId - Subscription ID
     * @returns {Promise} Toggle result（含 auto_renew 新状态）
     */
    toggleAutoRenew: async (subscriptionId, autoRenew) => {
        try {
            const response = await apiClient.post('/api/user/subscriptions/toggle-auto-renew', {
                id: subscriptionId,
                ...(autoRenew === undefined ? {} : { auto_renew: autoRenew ? 1 : 0 })
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** Turn auto-renew on for all (or the given) own subscriptions that have it off. */
    enableAutoRenewAll: async (ids = null) => {
        try {
            const response = await apiClient.post('/api/user/subscriptions/auto-renew-all', ids ? { ids } : {});
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** Subscriptions with auto-renew off that expire within 30 days (+ payment source). */
    getAutoRenewSummary: async () => {
        try {
            const response = await apiClient.get('/api/user/subscriptions/auto-renew-summary');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },
};

export default billingService;
